import fileService from '../services/fileService';

// File service functions

/**
 * Get presigned URL (with authentication)
 * @param {File} file - The file to get presigned URL for
 * @param {boolean} isProfile - Whether this is a profile upload
 * @returns {Promise<Object>} Promise that resolves to response object
 */
export const getPresignedUrl = async (file, isProfile = false) => {
    try {
        const response = await fileService.getPresignedUrl(file, isProfile);
        if (response.success) {
            return { success: true, data: response.data };
        } else {
            return { success: false, error: response.message };
        }
    } catch (error) {
        return { success: false, error: error.message };
    }
};

/**
 * Get presigned URL without authentication
 * @param {File} file - The file to get presigned URL for
 * @param {boolean} isProfile - Whether this is a profile upload
 * @param {string} module - Module name for the upload
 * @returns {Promise<Object>} Promise that resolves to response object
 */
export const getPresignedUrlWithoutAuth = async (file, isProfile = false, module) => {
    try {
        const response = await fileService.getPresignedUrlWithoutAuth(file, isProfile, module);
        if (response.success) {
            return { success: true, data: response.data };
        } else {
            return { success: false, error: response.message };
        }
    } catch (error) {
        return { success: false, error: error.message };
    }
};

/**
 * Get signed URLs
 * @param {string[]} fileKeys - Array of file keys to get signed URLs for
 * @param {boolean} isProfile - Whether these are profile files
 * @returns {Promise<Object>} Promise that resolves to response object
 */
export const getSignedUrls = async (fileKeys, isProfile = false) => {
    try {
        const response = await fileService.getSignedUrls(fileKeys, isProfile);
        if (response.success) {
            return { success: true, data: response.data };
        } else {
            return { success: false, error: response.message };
        }
    } catch (error) {
        return { success: false, error: error.message };
    }
};

/**
 * Get multipart presigned URLs
 * @param {string} contentType - The content type of the file
 * @param {number} fileSize - Size of the file in bytes
 * @param {number} partCount - Number of parts to split the file into
 * @returns {Promise<Object>} Promise that resolves to response object
 */
export const getMultipartPresignedUrls = async (contentType, fileSize, partCount) => {
    try {
        const response = await fileService.getMultipartPresignedUrls(contentType, fileSize, partCount);
        if (response.success) {
            return { success: true, data: response.data };
        } else {
            return { success: false, error: response.message };
        }
    } catch (error) {
        return { success: false, error: error.message };
    }
};

/**
 * Upload file part
 * @param {string} url - Presigned URL for the part
 * @param {Blob} part - The file part to upload
 * @param {string} contentType - Content type of the file
 * @returns {Promise<Object>} Promise that resolves to response object
 */
export const uploadPart = async (url, part, contentType) => {
    try {
        const response = await fileService.uploadPart(url, part, contentType);
        if (response.success) {
            return { success: true, data: response.data };
        } else {
            return { success: false, error: response.message };
        }
    } catch (error) {
        return { success: false, error: error.message };
    }
};

/**
 * Complete multipart upload
 * @param {string} key - The file key
 * @param {string} uploadId - The upload ID
 * @param {Array<{ETag: string, PartNumber: number}>} parts - Array of uploaded parts
 * @returns {Promise<Object>} Promise that resolves to response object
 */
export const completeMultipartUpload = async (key, uploadId, parts) => {
    try {
        const response = await fileService.completeMultipartUpload(key, uploadId, parts);
        if (response.success) {
            return { success: true, data: response.data };
        } else {
            return { success: false, error: response.message };
        }
    } catch (error) {
        return { success: false, error: error.message };
    }
};

// Note: clearFileErrors and setFileUploadProgress functions removed as they were Redux-specific action creators
